/**
 * Página de Aviso Legal - Estilo Awwwards
 */

import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { useTranslation, Trans } from 'react-i18next';
import { AnimatedSection } from '../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de Aviso Legal con estilo Awwwards
 * @returns {JSX.Element} Página de aviso legal inmersiva
 */
const LegalPage = () => {
  const { t } = useTranslation('legal');
  const heroRef = useRef(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-[60vh] flex items-end pb-24 md:pb-32 overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen en patrón repetido (tile) */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />

        {/* Contenido del hero */}
        <div className="relative z-10 container mx-auto px-4 md:px-8">
          <div className="max-w-4xl">
            <h1 className="hero-title font-baskerville font-bold text-white text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] mb-8 drop-shadow-lg">
              <Trans
                i18nKey="notice.hero.title"
                ns="legal"
                components={{ hl: <span className="text-intercasa-yellow" /> }}
              />
            </h1>
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light">
              {t('notice.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection animation="fadeUp">
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  {t('notice.general.title')}
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed mb-6">
                  {t('notice.general.intro')}
                </p>
                <div className="bg-intercasa-bone p-8 rounded-2xl border-l-4 border-intercasa-red">
                  <p className="text-intercasa-dark mb-3 text-lg"><strong>{t('notice.general.holder.label')}</strong> {t('notice.general.holder.value')}</p>
                  <p className="text-intercasa-dark mb-3 text-lg"><strong>{t('notice.general.cif.label')}</strong> {t('notice.general.cif.value')}</p>
                  <p className="text-intercasa-dark mb-3 text-lg"><strong>{t('notice.general.address.label')}</strong> {t('notice.general.address.value')}</p>
                  <p className="text-intercasa-dark text-lg"><strong>{t('notice.general.email.label')}</strong> {t('notice.general.email.value')}</p>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.1}>
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  {t('notice.purpose.title')}
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  {t('notice.purpose.text')}
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.2}>
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  {t('notice.intellectualProperty.title')}
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  {t('notice.intellectualProperty.text')}
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.3}>
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  {t('notice.liability.title')}
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  {t('notice.liability.text')}
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.4}>
              <div>
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  {t('notice.law.title')}
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  {t('notice.law.text')}
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default LegalPage;
